import React, { Component, Fragment } from "react";
import { PropTypes } from "prop-types";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import ReactTable from "react-table";
import debounce from "lodash/debounce";
import { NotificationManager } from "react-notifications";
import { Col, Row, } from "react-bootstrap";
import moment from "moment";
import Materialicon from "../../helpers/Materialicon";
import ProjectsService from "../../../services/ProjectsService";
import FixedCostsService from "../../../services/FixedCostsService";
import ProjectInfoCart from "./ProjectInfoCart";
import ProjectProgressCart from "./ProjectProgressCart";
import ProjectChart from "./ProjectChart";
import HandleError from "../../../services/HandleError";
import LoadingSVG from "../../helpers/LoadingSVG";
import LoaderInline from "../../helpers/LoaderInline";
import { minutesToHourAndMinutes, dataToSelectOption } from "../../helpers/HelperFunctions";
import { hasPermission, isSuperAdmin, hasRole } from "../../helpers/Permissions";

class ProjectInfo extends Component {
	state = {
		isLoadingTable: true,
		isLoadingFixedCosts: false,
		isLoadingProjectData: false,
		isReCalculating: false,
		pages: 1,
		projectInfo: {},
		chartData: { datasets: [], labels: [] },
		data: [],
		areas: [],
		fixedCosts: [],
		hasFixedCostPermission: hasPermission("fixed-cost.show"),
		isSuperadmin: isSuperAdmin(this.props.userLoginData.user),
		isAssistant: hasRole(6),
		logsTableColumns: [
			{
				Header: "Employee",
				accessor: "logged_by",
				Cell: row => <div className="text-center">{row.value}</div>,
				minWidth: 150
			}, {
				Header: "Task",
				accessor: "task_name",
				Cell: row => <div className="text-center">{row.value}</div>,
				minWidth: 200
			}, {
				Header: "Description",
				accessor: "description",
				Cell: row => <div className="text-center">{row.value}</div>,
				minWidth: 300
			},
			{
				Header: "Time",
				accessor: "time",
				Cell(row) {
					const { hours, minutes } = minutesToHourAndMinutes(row.value);
					return (<div className="text-center">{hours}h {minutes > 0 ? ` and ${minutes}min` : null}</div>);
				},
				minWidth: 200
			},
			{
				Header: "Logged At",
				accessor: "logged_at",
				Cell: row => <div className="text-center">{moment(row.value).format("DD-MM-YYYY")}</div>,
				minWidth: 150
			},
			{
				Header: "Created at",
				accessor: "created_at",
				Cell: row => <div className="text-center">{moment(row.value).format("DD-MM-YYYY HH:mm")}</div>,
				minWidth: 150
			},
		]
	}

	componentDidMount = () => {
		this.getProjectData();
		if (this.state.isSuperadmin && !this.state.isAssistant) {
			this.setState({
				logsTableColumns: [...this.state.logsTableColumns, {
					Header: "Cost",
					accessor: "total_cost",
					Cell: row => <div className="text-center">{row.value}€</div>,
					minWidth: 150
				}]
			});
		}
	}

	getProjectData = () => {
		const { projectId } = this.props.match.params;
		const projectDataService = new ProjectsService(this, "/info");
		this.setState({ isLoadingProjectData: true });
		projectDataService.show(projectId).then((res) => {
			const { project } = res.data;
			if (project) {
				this.setState({
					isLoadingProjectData: false,
					client_id: project.client_id || null,
					project_id: project.id || null,
					projectInfo: {
						name: project.name || "",
						budget: project.budget,
						costs: project.total_cost,
						benefit: project.benefit,
						progress: project.progress,
						status: project.status,
						type: project.type
					},
					chartData: {
						labels: ["Budget", "Costs"],
						datasets: [project.budget, project.total_cost]
					},
					areasProgress: project.areasProgress || [],
					areas: project.areas && project.areas.map((area) => ({ value: area.id || null, label: area.name || "" })),
				});
			}
		}).catch((err) => {
			this.setState({ isLoadingProjectData: false });
			HandleError(err);
		});
	}

	getProjectLogs = (state) => {
		const { projectId } = this.props.match.params;
		const { pageSize, sorted, filtered, page } = state;
		const data = JSON.stringify({ pageSize, sorted, filtered, page });
		const projectLogsService = new ProjectsService(this, `/${projectId}/logged-hours`);
		this.setState({ isLoadingTable: true });
		projectLogsService.index(page + 1, `data=${data}`).then((res) => {
			this.setState({
				isLoadingTable: false,
				data: res.data.data,
				pages: res.data.last_page,
				projectsPageState: state
			});
		}).catch((err) => {
			this.setState({ isLoadingTable: false });
			HandleError(err);
		});
	}

	getProjectFixedCosts = (state) => {
		if (this.state.hasFixedCostPermission) {
			const { projectId } = this.props.match.params;
			const { pageSize, sorted, filtered, page } = state;
			const data = JSON.stringify({ pageSize, sorted, filtered, page });
			this.setState({ isLoadingFixedCosts: true });
			new FixedCostsService(this, `?project_id=${projectId}`).index(page + 1, `data=${data}`).then((res) => {
				this.setState({
					isLoadingFixedCosts: false,
					fixedCosts: res.data.data,
					pages: res.data.last_page,
					fixedCostsPageState: state
				});
			}).catch((err) => {
				this.setState({ isLoadingFixedCosts: false });
				HandleError(err, this);
			});
		}
	}

	reCalculate = () => {
		const { projectId } = this.props.match.params;
		this.setState({ isReCalculating: true });
		new ProjectsService(this, `/${projectId}/calculate-costs`).index(0).then((res) => {
			const { project } = res.data;
			if (project) {
				this.setState({
					...this.state,
					isReCalculating: false,
					projectInfo: {
						name: project.name || "",
						budget: project.budget,
						costs: project.total_cost,
						benefit: project.benefit,
						progress: project.progress,
						status: project.status,
						type: project.type
					},
					chartData: {
						...this.state.chartData,
						datasets: [project.budget, project.total_cost]
					},
					areasProgress: project.areasProgress || [],
				});
			}
			this.getProjectLogs(this.state.projectsPageState);
			this.getProjectFixedCosts(this.state.fixedCostsPageState);
		}).catch((err) => {
			this.setState({ isReCalculating: false });
			HandleError(err, this);
		});
	}

	render() {
		const { name, progress, budget, costs, benefit, status, type } = this.state.projectInfo;
		const { data, chartData, isLoadingTable, isLoadingProjectData, pages, isLoadingFixedCosts,
			fixedCosts, isReCalculating, project_id, client_id, areasProgress,
			hasFixedCostPermission, isSuperadmin, logsTableColumns
		} = this.state;

		return (
			<div className="crud-page project-info">
				<div className="container-fluid">
					<section className="actions">
						<Row>
							<Col md={6} sm={6} xs={12}>
								<h2 className="page-title">{name}</h2>
							</Col>
							<Col md={6} sm={6} xs={12}>
								<Link to="/projects" className="pull-right btn-add-big btn btn-default">
									<Materialicon icon="keyboard_arrow_left" /> Back to Projects
    							</Link>
							</Col>
						</Row>
					</section>
					<div className="card card-top-border connection-card fadeInLoadUp">
						{isLoadingProjectData ? <LoadingSVG /> :
							<Fragment>
								<Row className="mb-25">
									<Col md={7}>
										<ul className="btns-left">
											<li><button disabled={isReCalculating} className="btn btn-primary btn-project" onClick={() => this.reCalculate()}> {isReCalculating ? <LoaderInline width="20" height="20" color="#fff" /> : "Recalculate"}</button></li>
											<li><Link to={`/projects/${project_id}/edit`} className="btn btn-primary btn-project "> Edit project </Link></li>
											<li><Link to={`/users/${client_id}`} className="btn btn-primary btn-project"> View customer </Link></li>
										</ul>
									</Col>
									<Col md={5}>
										<ul className="btns-right">
											{(type && type !== "package") &&
												<li>
													<Link
														className="btn btn-info btn-project"
														to={{
															pathname: "/projects/create-project", state: {
																type: "extra-hours",
																project: {
																	id: project_id,
																	name,
																	client_id
																}
															}
														}}
													>
														Add extra hours
													</Link>
												</li>
											}
											{((status && status === "Active") && (type && type !== "package")) &&
												<li>
													<Link
														className="btn btn-info btn-project"
														to={{
															pathname: "/tasks/create-task",
															state: {
																project: {
																	value: project_id,
																	label: name,
																}
															}
														}}
													>
														Add new task
													</Link>
												</li>
											}
										</ul>
									</Col>
								</Row>

								{/* Project budget, cost, benefit, */}
								{isSuperadmin &&
									<Row>
										<Col md={4}><ProjectInfoCart icon="euro_symbol" text="budget" value={budget} /></Col>
										<Col md={4}><ProjectInfoCart icon="euro_symbol" text="costs" value={costs} /></Col>
										<Col md={4}><ProjectInfoCart icon="euro_symbol" text="benefits" value={benefit} /></Col>
									</Row>
								}

								{/* project progress */}
								<Row>
									{isSuperadmin &&
										<Col md={4}>
											<ProjectChart chartData={chartData} />
										</Col>
									}
									<div className={isSuperadmin ? "col-md-8" : "col-md-12"}>
										<Row>
											<Col md={12}>
												<ProjectProgressCart
													progress={progress}
													name="Project progress"
													class="project-progress"
												/>
											</Col>
										</Row>
										<Row>
											{(areasProgress && Array.isArray(areasProgress)) ?
												<React.Fragment>
													{areasProgress.map((item) => (
														<Col md={6} key={item.area_id}>
															<ProjectProgressCart
																class="area-progress"
																progress={parseFloat(item.progress)}
																name={item.name}
															/>
														</Col>
													))}
												</React.Fragment>
												:
												null
											}
										</Row>
									</div>
								</Row>
							</Fragment>
						}


						{/* project table */}
						<div className="project-tbl">
							<h3 className="tbl-title">Project Logs</h3>
							<ReactTable
								columns={logsTableColumns}
								manual
								data={data}
								pages={pages}
								loading={isLoadingTable}
								onFetchData={debounce(this.getProjectLogs, 200)}
								filterable
								defaultSorted={[{ id: "logged_at", desc: true }]}
								defaultPageSize={10}
								className="-highlight project-logs"
							/>
						</div>
						{hasFixedCostPermission &&
							<div className="project-tbl">
								<h3 className="tbl-title">Project fixed costs</h3>
								<ReactTable
									columns={[
										{
											Header: "Name",
											accessor: "name",
											Cell: row => <div className="text-center">{row.value}</div>,
											minWidth: 150
										},
										{
											Header: "Cost",
											accessor: "cost",
											Cell: row => <div className="text-center">{row.value}€</div>,
											minWidth: 150
										}, {
											Header: "Month",
											accessor: "month_name",
											Cell: row => <div className="text-center">{row.value}</div>
										}, {
											Header: "Created at",
											accessor: "created_at",
											Cell: row => <div className="text-center">{moment(row.value).format("DD-MM-YYYY")}</div>,
											minWidth: 150
										},
									]}
									manual
									data={fixedCosts}
									pages={pages}
									loading={isLoadingFixedCosts}
									onFetchData={debounce(this.getProjectFixedCosts, 200)}
									filterable
									defaultSorted={[{ id: "created_at", desc: true }]}
									defaultPageSize={10}
									className="-highlight project-fixed-costs"
								/>
							</div>
						}
					</div>
				</div>
			</div>
		);
	}
}

const UserData = (state) => ({
	userLoginData: state.userLoginData,
});

ProjectInfo.propTypes = {
	match: PropTypes.shape({
		params: PropTypes.shape({
			projectId: PropTypes.string.isRequired
		})
	}).isRequired,
	userLoginData: PropTypes.shape({
		user: PropTypes.shape({
			roles: PropTypes.arrayOf(
				PropTypes.object
			)
		}).isRequired
	}).isRequired,
};

export default connect(UserData)(withRouter(ProjectInfo));


// WEBPACK FOOTER //
// ./src/components/pages/projects/ProjectInfo.js